import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { POOL_NAME } from '@/constants';
import { refreshTokenConfig } from '@/oidcConfig';
import { clearOnLogOut, refreshSign } from '@/slices/authSlice';
import { RootState } from '@/store';

export const useTokenAutoRefresh = () => {
  const dispatch = useDispatch();
  const authSlice = useSelector((state: RootState) => state.auth);
  const authSliceMessage = authSlice?.status?.message;
  const {
    accessToken,
    refreshToken,
    isOidcClient,
    username: authUsername,
    poolName: authPoolName,
    status: authError
  } = authSlice;
  const previousAccessTokenRef = useRef(accessToken);
  const poolName = authPoolName || POOL_NAME;

  const user = useSelector((state: RootState) => state.userProfile);
  const userMessage = user?.status?.message;
  const username = authUsername || user?.username || '';

  const auditableElements = useSelector(
    (state: RootState) => state.auditableElements
  );
  const auditableElementsMessage = auditableElements?.status?.message;

  useEffect(() => {
    if (isOidcClient) return;
    if (!refreshToken) return;
    if (!username) return;
    if (authSliceMessage === 'loading') return;
    if (auditableElementsMessage === 'loading') return;
    if (userMessage === 'loading') return;

    const { refreshTime, expiresInSec } = refreshTokenConfig(authSlice);

    if (expiresInSec <= 60) {
      dispatch(
        refreshSign({
          refreshToken,
          username,
          poolName
        }) as any
      );
      return;
    }

    const revokeTokenTimer = setTimeout(() => {
      dispatch(
        refreshSign({
          refreshToken,
          username,
          poolName
        }) as any
      );
    }, refreshTime);

    return () => clearTimeout(revokeTokenTimer);
  }, [
    refreshToken,
    username,
    poolName,
    authSliceMessage,
    dispatch,
    userMessage,
    auditableElementsMessage
  ]);

  useEffect(() => {
    if ([authError?.code].includes(401)) {
      dispatch(clearOnLogOut() as any);
    }
  }, [authError?.code, dispatch]);

  useEffect(() => {
    const previousAccessToken = previousAccessTokenRef.current;
    previousAccessTokenRef.current = accessToken;

    // Only clear when a classic/OIDC session is lost — not on initial empty boot
    // (multiApp handoff must be able to establish a classic session first).
    if (previousAccessToken && !accessToken) {
      dispatch(clearOnLogOut() as any);
    }
  }, [accessToken, dispatch]);
};
